import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ArrowLeft, Search, Download, Filter, RefreshCw, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { AuthorizationDialog } from '@/components/ui/authorization-dialog';
import { useAutoRefresh } from '@/hooks/use-auto-refresh';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function EmployeeActionsLedger() {
  const navigate = useNavigate();
  
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  
  // Filter states
  const [filters, setFilters] = useState({
    user_id: '',
    action_type: '',
    module_name: '',
    entity_type: '',
    start_date: '',
    end_date: '',
    limit: 50,
    skip: 0
  });
  
  const [viewDetails, setViewDetails] = useState(null);
  
  // Delete states
  const [logToDelete, setLogToDelete] = useState(null);
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  
  // Predefined options - COMPREHENSIVE LIST
  const actionTypes = [
    'CREATE',
    'UPDATE', 
    'DELETE',
    'APPROVE',
    'REJECT',
    'INWARD',
    'OUTWARD',
    'UTILIZE',
    'START',
    'PAUSE',
    'STOP',
    'RESUME',
    'COMPLETE',
    'LOGIN',
    'LOGOUT',
    'PERMISSIONS_UPDATE',
    'REVOKE_ACCESS',
    'GRANT_ACCESS'
  ];
  
  const moduleNames = [
    'Master Data - Fabric',
    'Master Data - Trims',
    'Master Data - Packaging',
    'Master Data - Article',
    'Master Data - Digital Design',
    'Fashion Design',
    'Production Orders',
    'Purchase Orders',
    'User Management',
    'User Permissions',
    'Sampling Time Tracker',
    'Wastage',
    'Gate Pass',
    'Expenses',
    'Inventory - Raw Goods',
    'Inventory - Finished Goods',
    'Work Orders',
    'External Work Orders',
    'Pre-Production Costing',
    'Production Planning',
    'Quality Control',
    'Cutting Room',
    'Sewing Floor',
    'Finishing & Packing',
    'Delivery Management',
    'Showroom',
    'Tech Pack'
  ];

  useEffect(() => {
    fetchLogs();
  }, [filters.skip, filters.limit]);

  useEffect(() => {
    fetchUsers();
  }, []); // Run once on mount to fetch users

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      console.log('Fetching users with token:', token ? 'Token exists' : 'No token');
      
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Users API response:', response.data);
      console.log('Number of users:', response.data?.length);
      
      if (response.data && Array.isArray(response.data)) {
        setUsers(response.data);
        console.log('Users set successfully:', response.data.length, 'users');
      } else {
        console.error('Invalid users data format:', response.data);
        toast.error('Invalid user data format received');
      }
    } catch (error) {
      console.error('Error fetching users:', error.response || error);
      toast.error(`Failed to fetch users: ${error.response?.data?.detail || error.message}`);
    }
  };

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Build query params
      const params = {};
      if (filters.user_id) params.user_id = filters.user_id;
      if (filters.action_type) params.action_type = filters.action_type;
      if (filters.module_name) params.module_name = filters.module_name;
      if (filters.entity_type) params.entity_type = filters.entity_type;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      params.limit = filters.limit;
      params.skip = filters.skip;
      
      const response = await axios.get(`${API}/audit-logs`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      
      setLogs(response.data.logs);
      setTotal(response.data.total);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching logs:', error);
      toast.error('Failed to fetch audit logs');
      setLoading(false);
    }
  };

  // Auto-refresh every 5 seconds, but pause when viewing details
  const isUserEditing = !!viewDetails || showAuthDialog;
  useAutoRefresh(fetchLogs, 5, isUserEditing);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, skip: 0 })); // Reset to first page when filtering
  };

  const handleApplyFilters = () => {
    fetchLogs();
  };

  const handleReset = () => {
    setFilters({
      user_id: '',
      action_type: '',
      module_name: '',
      entity_type: '',
      start_date: '',
      end_date: '',
      limit: 50,
      skip: 0
    });
    setTimeout(() => fetchLogs(), 100);
  };

  const handleDeleteClick = (log) => {
    setLogToDelete(log);
    setShowAuthDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!logToDelete) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/audit-logs/${logToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Audit log deleted successfully');
      
      // Refresh logs
      await fetchLogs();
      
      setShowAuthDialog(false);
      setLogToDelete(null);
    } catch (error) {
      console.error('Error deleting audit log:', error);
      toast.error('Failed to delete audit log');
    }
  };

  const handleNextPage = () => {
    setFilters(prev => ({ ...prev, skip: prev.skip + prev.limit }));
  };

  const handlePrevPage = () => {
    setFilters(prev => ({ ...prev, skip: Math.max(0, prev.skip - prev.limit) }));
  };

  const getActionBadgeColor = (action) => {
    const colors = {
      'CREATE': 'bg-green-100 text-green-800',
      'UPDATE': 'bg-blue-100 text-blue-800',
      'DELETE': 'bg-red-100 text-red-800',
      'APPROVE': 'bg-purple-100 text-purple-800',
      'INWARD': 'bg-yellow-100 text-yellow-800',
      'UTILIZE': 'bg-orange-100 text-orange-800',
      'START': 'bg-teal-100 text-teal-800',
      'PAUSE': 'bg-gray-100 text-gray-800',
      'STOP': 'bg-pink-100 text-pink-800'
    };
    return colors[action] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-PK', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <button onClick={() => navigate('/user-access-control')} className="p-2 hover:bg-gray-200 rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-3xl font-bold">Employee Actions Ledger</h1>
          </div>
          <Button onClick={handleReset} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" /> Reset Filters
          </Button>
        </div>

        {/* Filters */}
        <div className="bg-white p-6 rounded-lg shadow-md mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            {/* Employee Filter */}
            <div>
              <Label>Employee</Label>
              <SearchableSelect
                value={filters.user_id}
                onValueChange={(value) => handleFilterChange('user_id', value)}
                placeholder="Select employee..."
                options={users.map(user => ({
                  value: user.id,
                  label: `${user.name} (${user.email || user.username})`
                }))}
                emptyMessage="No employees found"
              />
              {users.length === 0 && (
                <p className="text-xs text-red-500 mt-1">⚠️ No users loaded - check console</p>
              )}
            </div>

            {/* Action Type Filter */}
            <div>
              <Label>Action Type</Label>
              <SearchableSelect
                value={filters.action_type}
                onValueChange={(value) => handleFilterChange('action_type', value)}
                placeholder="Select action type..."
                options={actionTypes.map(action => ({
                  value: action,
                  label: action
                }))}
                emptyMessage="No actions found"
              />
            </div>

            {/* Module Filter */}
            <div>
              <Label>Module</Label>
              <SearchableSelect
                value={filters.module_name}
                onValueChange={(value) => handleFilterChange('module_name', value)}
                placeholder="Select module..."
                options={moduleNames.map(module => ({
                  value: module,
                  label: module
                }))}
                emptyMessage="No modules found"
              />
            </div>

            {/* Entity Type Filter */}
            <div>
              <Label>Entity Type</Label>
              <Input
                type="text"
                placeholder="e.g., Fabric, User, Design"
                value={filters.entity_type}
                onChange={(e) => handleFilterChange('entity_type', e.target.value)}
              />
            </div>

            {/* Date Range */}
            <div>
              <Label>Start Date</Label>
              <Input
                type="date"
                value={filters.start_date}
                onChange={(e) => handleFilterChange('start_date', e.target.value)}
              />
            </div>

            <div>
              <Label>End Date</Label>
              <Input
                type="date"
                value={filters.end_date}
                onChange={(e) => handleFilterChange('end_date', e.target.value)}
              />
            </div>
          </div>

          <Button onClick={handleApplyFilters} className="w-full">
            <Filter className="w-4 h-4 mr-2" /> Apply Filters
          </Button>
        </div>

        {/* Results Info */}
        <div className="bg-white p-4 rounded-lg shadow-md mb-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600">
              Showing {filters.skip + 1} - {Math.min(filters.skip + filters.limit, total)} of {total} records
            </p>
            <div className="flex space-x-2">
              <Button
                onClick={handlePrevPage}
                disabled={filters.skip === 0}
                variant="outline"
                size="sm"
              >
                Previous
              </Button>
              <Button
                onClick={handleNextPage}
                disabled={filters.skip + filters.limit >= total}
                variant="outline"
                size="sm"
              >
                Next
              </Button>
            </div>
          </div>
        </div>

        {/* Logs Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">Loading...</div>
          ) : logs.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No audit logs found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-100 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Timestamp</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Employee</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Action</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Module</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Entity</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Description</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {logs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm">{formatDate(log.timestamp)}</td>
                      <td className="px-4 py-3 text-sm font-medium">{log.user_name}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${getActionBadgeColor(log.action_type)}`}>
                          {log.action_type}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">{log.module_name}</td>
                      <td className="px-4 py-3 text-sm">
                        {log.entity_type}
                        {log.entity_code && <span className="text-gray-500 ml-1">({log.entity_code})</span>}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{log.description}</td>
                      <td className="px-4 py-3">
                        <Button
                          onClick={() => setViewDetails(log)}
                          variant="outline"
                          size="sm"
                        >
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Details Dialog */}
      <Dialog open={!!viewDetails} onOpenChange={() => setViewDetails(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Action Details</DialogTitle>
          </DialogHeader>
          {viewDetails && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-600">Timestamp</Label>
                  <p className="font-medium">{formatDate(viewDetails.timestamp)}</p>
                </div>
                <div>
                  <Label className="text-gray-600">Employee</Label>
                  <p className="font-medium">{viewDetails.user_name} ({viewDetails.user_email})</p>
                </div>
                <div>
                  <Label className="text-gray-600">Action Type</Label>
                  <span className={`px-2 py-1 rounded text-xs font-semibold ${getActionBadgeColor(viewDetails.action_type)}`}>
                    {viewDetails.action_type}
                  </span>
                </div>
                <div>
                  <Label className="text-gray-600">Module</Label>
                  <p className="font-medium">{viewDetails.module_name}</p>
                </div>
                <div>
                  <Label className="text-gray-600">Entity Type</Label>
                  <p className="font-medium">{viewDetails.entity_type}</p>
                </div>
                <div>
                  <Label className="text-gray-600">Entity Code/ID</Label>
                  <p className="font-medium">{viewDetails.entity_code || viewDetails.entity_id}</p>
                </div>
              </div>
              
              <div>
                <Label className="text-gray-600">Description</Label>
                <p className="font-medium mt-1">{viewDetails.description}</p>
              </div>

              {viewDetails.old_data && (
                <div>
                  <Label className="text-gray-600">Previous Data</Label>
                  <pre className="bg-gray-50 p-3 rounded mt-1 text-xs overflow-auto">
                    {JSON.stringify(viewDetails.old_data, null, 2)}
                  </pre>
                </div>
              )}

              {viewDetails.new_data && (
                <div>
                  <Label className="text-gray-600">New Data</Label>
                  <pre className="bg-gray-50 p-3 rounded mt-1 text-xs overflow-auto">
                    {JSON.stringify(viewDetails.new_data, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default EmployeeActionsLedger;
